import React, { useEffect, useState, useMemo } from "react";
import InsightButton from "../components/InsightButton";
import { collection, query, where, onSnapshot, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "../contexts/OrgContext";
import { useCanSee } from "../hooks/useCanSee";
import { ChevronLeft, ChevronRight, Users, BarChart2, Plus, X, AlertTriangle } from "lucide-react";
import { usePiano, BadgePiano } from "../utils/usePiano";
import SquadraEditor, { normalizzaSquadra, squadraTotale, squadraToString } from "../components/SquadraEditor";
import { isNoleggioAttivo, TIPI_MEZZO_OPERA, TIPI_VEICOLO, toLocalDateStr } from "../utils/noleggiUtils";

const GIORNI = ["Lun","Mar","Mer","Gio","Ven","Sab","Dom"];
const LIVELLI_PROF = {
  1:{ label:"Inesperto",     short:"Liv.1", col:"#6b7280" },
  2:{ label:"Poco esperto",  short:"Liv.2", col:"#d97706" },
  3:{ label:"Autonomo",      short:"Liv.3", col:"#2563eb" },
  4:{ label:"Esperto",       short:"Liv.4", col:"#7c3aed" },
  5:{ label:"Molto esperto", short:"Liv.5", col:"#059669" },
};
const COLORI_COMMESSA = ["#7c3aed","#2563eb","#059669","#d97706","#dc2626","#0891b2","#db2777","#10b981"];

function getLunedi(date){
  const d = new Date(date); const day = d.getDay();
  d.setDate(d.getDate()+(day===0?-6:1-day)); d.setHours(0,0,0,0); return d;
}
function addDays(d,n){ const r=new Date(d); r.setDate(r.getDate()+n); return r; }
function fmtData(d){ return d.toLocaleDateString("it-IT",{day:"numeric",month:"short"}); }
function getSettKey(lunedi,orgId){
  return `${orgId}_${lunedi.getFullYear()}${String(lunedi.getMonth()+1).padStart(2,"0")}${String(lunedi.getDate()).padStart(2,"0")}`;
}

export default function PianificazioneTeamPage({ onNavigate }) {
  const { currentUser, isOrgAdmin, isPlatformOwner } = useAuth();
  const canSee = useCanSee();
  const { effectiveOrgId } = useOrg();
  const { piano, giorniRimasti, solaLettura } = usePiano(effectiveOrgId);
  const isReadOnly = solaLettura("pianificazione");

  const [utenti, setUtenti] = useState([]);
  const [infoFontiAperto, setInfoFontiAperto] = useState(() => localStorage.getItem("nexiora_infoFontiChiuso") !== "true");
  const [programmazioniRaw, setProgrammazioniRaw] = useState({});
  const [projects, setProjects] = useState([]);
  const [settimaneCaricate, setSettimaneCaricate] = useState([]);

  // Settimane da mostrare: corrente + 7 successive
  const oggi = useMemo(()=>new Date(),[]);
  const settimane = useMemo(()=>{
    const lunediOggi = getLunedi(oggi);
    return Array.from({length:8},(_,i)=>{
      const lun = addDays(lunediOggi, i*7);
      return {
        lunedi: lun,
        giorni: Array.from({length:7},(_,g)=>addDays(lun,g)),
        key: getSettKey(lun, effectiveOrgId||""),
        label: `${fmtData(lun)} — ${fmtData(addDays(lun,4))}`,
      };
    });
  },[oggi, effectiveOrgId]);

  const [dipendenti, setDipendenti] = useState([]);
  const [commessePreesistenti, setCommessePreesistenti] = useState([]);
  const [manutenzionePiani, setManutenzionePiani] = useState([]);
  const [showPreesistenti, setShowPreesistenti] = useState(false);
  const [nuovaPreesistente, setNuovaPreesistente] = useState({ nome:"", dataFine:"" });
  const [squadraPreesistente, setSquadraPreesistente] = useState([{livello:1,quantita:1}]);

  // Carica commesse preesistenti
  useEffect(()=>{
    if(!effectiveOrgId) return;
    return onSnapshot(
      query(collection(db,"commessePreesistenti"),where("orgId","==",effectiveOrgId)),
      snap=>setCommessePreesistenti(snap.docs.map(d=>({id:d.id,...d.data()})))
    );
  },[effectiveOrgId]);

  // Carica dipendenti (per livello professionalità)
  useEffect(()=>{
    if(!effectiveOrgId) return;
    import("firebase/firestore").then(({getDocs:gd,query:q2,collection:c2,where:w2})=>{
      gd(q2(c2(db,"dipendenti"),w2("organizationId","==",effectiveOrgId)))
        .then(snap=>setDipendenti(snap.docs.map(d=>({id:d.id,...d.data()}))));
    });
  },[effectiveOrgId]);

  // Carica utenti
  useEffect(()=>{
    if(!effectiveOrgId) return;
    return onSnapshot(query(collection(db,"users"),where("organizationId","==",effectiveOrgId),where("enabled","==",true)),
      snap=>{ const l=snap.docs.map(d=>d.data()); l.sort((a,b)=>(a.displayName||"").localeCompare(b.displayName||"","it")); setUtenti(l); });
  },[effectiveOrgId]);

  // Carica progetti
  useEffect(()=>{
    if(!effectiveOrgId) return;
    return onSnapshot(query(collection(db,"projects"),where("organizationId","==",effectiveOrgId),where("statoCommessa","==","attiva")),
      snap=>setProjects(snap.docs.map(d=>({id:d.id,...d.data()})).filter(p=>!p.archived&&!p.isParent)));
  },[effectiveOrgId]);

  // Carica Piani di Manutenzione con tecnici assegnati (per il carico di lavoro ricorrente)
  useEffect(()=>{
    if(!effectiveOrgId) return;
    return onSnapshot(query(collection(db,"manutenzionePiani"),where("organizationId","==",effectiveOrgId)),
      snap=>setManutenzionePiani(snap.docs.map(d=>({id:d.id,...d.data()}))
        .filter(p=>(p.tecnici||[]).length>0 && p.decorrenza)));
  },[effectiveOrgId]);

  // Radar noleggi attivi — sostituisce il vecchio "colpo d'occhio" tenuto a mano: sempre
  // corretto perché legge dalla stessa fonte del Controllo Economico (Registro Noleggi).
  const [noleggiAttivi, setNoleggiAttivi] = useState([]);
  useEffect(()=>{
    if(!effectiveOrgId) return;
    return onSnapshot(query(collection(db,"noleggi"),where("orgId","==",effectiveOrgId)),
      snap=>setNoleggiAttivi(snap.docs.map(d=>({id:d.id,...d.data()})).filter(isNoleggioAttivo)));
  },[effectiveOrgId]);

  const radarNoleggi = useMemo(()=>{
    const mappa = {};
    noleggiAttivi.forEach(n=>{
      const key = n.tipo;
      if(!mappa[key]) mappa[key] = { tipo:n.tipo, count:0, commesse:new Set(), categoria:n.categoria };
      mappa[key].count += 1;
      if(n.projectNome) mappa[key].commesse.add(n.projectNome);
    });
    return Object.values(mappa).map(v=>({...v, commesse:Array.from(v.commesse)}));
  },[noleggiAttivi]);

  // Carica programmazioni per tutte le settimane
  useEffect(()=>{
    if(!effectiveOrgId||settimane.length===0) return;
    const unsubs = settimane.map(s=>{
      return onSnapshot(doc(db,"programmazioniSettimanali",s.key), snap=>{
        const celle = snap.exists()?(snap.data().celle||{}):{};
        setProgrammazioniRaw(prev=>({...prev,[s.key]:celle}));
      });
    });
    return ()=>unsubs.forEach(u=>u());
  },[effectiveOrgId, settimane]);

  // Colore per commessa
  const coloreCommessa = useMemo(()=>{
    const mappa = {};
    projects.forEach((p,i)=>{ mappa[p.name] = COLORI_COMMESSA[i%COLORI_COMMESSA.length]; });
    return mappa;
  },[projects]);

  // Analisi disponibilità per settimana — logica corretta e completa
  const analisiSettimane = useMemo(()=>{
    // ORGANICO OPERATIVO REALE: tutti i dipendenti con categoria operativa Operativo in anagrafica
    // Sono la fonte di verità della capacità aziendale, con o senza account Nexiora
    const operativiAnagrafica = dipendenti.filter(d => d.ruolo === "Operativo");

    // ── ORIZZONTE DI PIANIFICAZIONE REALE ───────────────────────────────────
    // Fin dove arriva, oggi, l'ultima settimana con almeno un dato vero in
    // Programmazione Settimanale? Entro quell'orizzonte una cella vuota vuol dire
    // "libero per davvero" (l'azienda pianifica in dettaglio fin qui). Oltre, una
    // cella vuota è ambigua — lì continuiamo a stimare dal piano di carico delle
    // commesse. Si adatta da sola a ogni organizzazione, senza nessuna soglia fissa
    // decisa a priori: un'azienda che pianifica un mese avanti sposta l'orizzonte
    // da sola, una che pianifica solo la settimana corrente pure.
    let orizzonteIdx = -1;
    settimane.forEach((sett, idx) => {
      const celleSett = programmazioniRaw[sett.key]||{};
      if (Object.values(celleSett).some(v => v.commessaNome)) orizzonteIdx = idx;
    });

    return settimane.map((sett, settIdx)=>{
      const oltreOrizzonte = settIdx > orizzonteIdx;
      const celle = programmazioniRaw[sett.key]||{};
      const lunediMs = sett.lunedi.getTime();
      const venerdiMs = addDays(sett.lunedi,4).getTime();

      // ── GRIGLIA PIANIFICAZIONE ─────────────────────────────────────────────
      // Mostra solo chi ha nexoraUid (può essere programmato settimanalmente)
      // e ha categoria operativa Operativo in anagrafica dipendenti
      const perUtente = {};
      utenti.forEach(u => {
        const dip = dipendenti.find(d => d.nexoraUid === u.uid);
        if (!dip) return; // utente non in anagrafica dipendenti
        if (dip.ruolo !== "Operativo") return; // non operativo
        perUtente[u.uid] = {
          uid: u.uid,
          nome: u.displayName || `${dip.nome||""} ${dip.cognome||""}`.trim(),
          livello: parseInt(dip.livelloProfessionalita)||0,
          giorni: {}, oreOccupate: 0, giorniOccupati: 0, daProgr: 0, daPiano: 0
        };
      });

      // ── PASSO 1: dati reali da programmazione settimanale ─────────────────
      Object.entries(celle).forEach(([key, val]) => {
        const parts = key.split("_");
        if (parts.length < 3) return;
        const uid = parts[0];
        const dateStr = parts.slice(2).join("_");
        if (!perUtente[uid]) return;
        if (val.commessaNome) {
          perUtente[uid].giorni[dateStr] = { commessaNome: val.commessaNome, ore: val.ore||8, reale: true };
          perUtente[uid].oreOccupate += parseFloat(val.ore||8);
          perUtente[uid].giorniOccupati++;
          perUtente[uid].daProgr++;
        }
      });

      // ── PASSO 2: stima da piano di carico per giorni non programmati ───────
      // Solo oltre l'orizzonte di pianificazione reale — entro l'orizzonte una
      // cella vuota è già un'informazione affidabile (davvero libero), stimarci
      // sopra creerebbe falsi "occupato" come quello segnalato da Paolo.
      if (oltreOrizzonte) {
      projects.forEach(p => {
        if (!p.dataInizio || !p.dataFine) return;
        const inizio = new Date(p.dataInizio+"T00:00:00").getTime();
        const fine = new Date(p.dataFine+"T00:00:00").getTime();
        if (fine < lunediMs || inizio > venerdiMs) return;

        const squadraP = normalizzaSquadra(p);
        if (squadraP.length === 0) return;

        // Assegna operai disponibili per livello (priorità ai più specializzati)
        const squadraOrdinata = [...squadraP].sort((a,b) => (b.livello||0)-(a.livello||0));
        const assegnatiUid = new Set();
        squadraOrdinata.forEach(op => {
          const livReq = parseInt(op.livello)||0;
          const uid = Object.keys(perUtente).find(u => {
            if (assegnatiUid.has(u)) return false;
            return livReq === 0 || (perUtente[u].livello >= livReq);
          });
          if (!uid) return;
          assegnatiUid.add(uid);
          sett.giorni.slice(0,5).forEach(g => {
            const gMs = g.getTime();
            if (gMs < inizio || gMs > fine) return;
            const ds = `${g.getFullYear()}-${String(g.getMonth()+1).padStart(2,"0")}-${String(g.getDate()).padStart(2,"0")}`;
            if (!perUtente[uid].giorni[ds]) {
              perUtente[uid].giorni[ds] = { commessaNome: p.name, ore: p.oreGiornaliere||8, reale: false, stimato: true };
              perUtente[uid].oreOccupate += p.oreGiornaliere||8;
              perUtente[uid].giorniOccupati++;
              perUtente[uid].daPiano++;
            }
          });
        });
      });
      } // fine oltreOrizzonte (PASSO 2)

      // ── PASSO 3: commesse preesistenti ────────────────────────────────────
      // Stessa regola: solo oltre l'orizzonte di pianificazione reale.
      if (oltreOrizzonte) {
      commessePreesistenti.forEach(cp => {
        if (!cp.dataFine) return;
        const fine = new Date(cp.dataFine+"T00:00:00").getTime();
        if (fine < lunediMs) return;
        const numNecessari = parseInt(cp.operai)||0;
        if (numNecessari === 0) return;
        let assegnati = 0;
        Object.keys(perUtente).forEach(uid => {
          if (assegnati >= numNecessari) return;
          const livOp = perUtente[uid].livello;
          if ((cp.livelloMin||0) > 0 && livOp < cp.livelloMin) return;
          sett.giorni.slice(0,5).forEach(g => {
            if (g.getTime() > fine) return;
            const ds = `${g.getFullYear()}-${String(g.getMonth()+1).padStart(2,"0")}-${String(g.getDate()).padStart(2,"0")}`;
            if (!perUtente[uid].giorni[ds]) {
              perUtente[uid].giorni[ds] = { commessaNome: cp.nome||"Pregressa", ore: 8, reale: false, stimato: true, pregressa: true };
              perUtente[uid].oreOccupate += 8;
              perUtente[uid].giorniOccupati++;
            }
          });
          assegnati++;
        });
      });
      } // fine oltreOrizzonte (PASSO 3)

      // ── PASSO 4: Piani di Manutenzione ricorrenti ───────────────────────────
      // Proietta le occorrenze future (decorrenza + frequenza ripetuta) e segna
      // i tecnici collegati come occupati — SOLO se il giorno non è già occupato
      // da un dato reale o da un'altra stima (mai sovrascrive dati più affidabili).
      const FREQ_GIORNI = { settimanale:7, mensile:30, trimestrale:90, semestrale:180, annuale:365 };
      manutenzionePiani.forEach(piano => {
        const giorniFreq = FREQ_GIORNI[piano.frequenza] || 30;
        const durataGiorni = Math.max(1, parseInt(piano.durataPrevista)||1);
        const decorrenza = new Date(piano.decorrenza+"T00:00:00");
        if (isNaN(decorrenza.getTime())) return;

        let occorrenza = new Date(decorrenza);
        const limiteMs = venerdiMs + giorniFreq*24*3600*1000;
        let iterazioni = 0;
        while (occorrenza.getTime() <= limiteMs && iterazioni < 500) {
          const occInizio = occorrenza.getTime();
          const occFine = occInizio + (durataGiorni-1)*24*3600*1000;
          if (occFine >= lunediMs && occInizio <= venerdiMs) {
            (piano.tecnici||[]).forEach(t => {
              const uid = t.uid;
              if (!uid || !perUtente[uid]) return; // tecnico non in anagrafica operativa: salta senza errori
              sett.giorni.slice(0,5).forEach(g => {
                const gMs = g.getTime();
                if (gMs < occInizio || gMs > occFine) return;
                const ds = `${g.getFullYear()}-${String(g.getMonth()+1).padStart(2,"0")}-${String(g.getDate()).padStart(2,"0")}`;
                if (!perUtente[uid].giorni[ds]) {
                  perUtente[uid].giorni[ds] = { commessaNome: `🔧 ${piano.nome}`, ore: 8, reale: false, stimato: true, manutenzione: true };
                  perUtente[uid].oreOccupate += 8;
                  perUtente[uid].giorniOccupati++;
                }
              });
            });
          }
          occorrenza.setDate(occorrenza.getDate() + giorniFreq);
          iterazioni++;
        }
      });

      // ── CALCOLO CAPACITÀ ──────────────────────────────────────────────────
      // Capacità massima = TUTTI gli operativi in anagrafica (non solo chi ha account Nexiora)
      // Questo è il tetto massimo reale dell'azienda
      const numOperativiTotali = operativiAnagrafica.length;
      const maxOreTeam = numOperativiTotali * 5 * 8; // 5 giorni lavorativi × 8 ore

      // Ore occupate da programmazione reale (chi ha account Nexiora)
      const oreReali = Object.values(perUtente).reduce((s,u) => s + u.oreOccupate, 0);

      // Ore occupate stimate da piano di carico commesse
      // (serve per operativi senza account Nexiora e per settimane future non programmate)
      let oreImpegnatePiano = 0;
      projects.forEach(p => {
        if (!p.dataInizio || !p.dataFine) return;
        const inizio = new Date(p.dataInizio+"T00:00:00").getTime();
        const fine = new Date(p.dataFine+"T00:00:00").getTime();
        if (fine < lunediMs || inizio > venerdiMs) return;
        const numOperai = normalizzaSquadra(p).length || parseInt(p.operaiNecessari)||0;
        if (numOperai === 0) return;
        const giorniEff = sett.giorni.slice(0,5).filter(g => {
          const gMs = g.getTime();
          return gMs >= inizio && gMs <= fine;
        }).length;
        oreImpegnatePiano += numOperai * giorniEff * (p.oreGiornaliere||8);
      });
      commessePreesistenti.forEach(cp => {
        if (!cp.dataFine) return;
        const fine = new Date(cp.dataFine+"T00:00:00").getTime();
        if (fine < lunediMs) return;
        const numOp = parseInt(cp.operai)||0;
        if (numOp === 0) return;
        const giorniEff = sett.giorni.slice(0,5).filter(g => g.getTime() <= fine).length;
        oreImpegnatePiano += numOp * giorniEff * 8;
      });

      // Ore totali occupate: usiamo SEMPRE il totale reale già calcolato persona per persona
      // (oreReali), che rispetta correttamente la precedenza "dato reale prima, stima solo
      // per i giorni ancora vuoti" — non un secondo calcolo grezzo che sommava le stime di
      // tutte le commesse attive senza verificare se le persone fossero già libere davvero.
      const oreTotaleOccupate = Math.min(oreReali, maxOreTeam);

      const percentualeOccupata = maxOreTeam > 0
        ? Math.min(100, Math.round(oreTotaleOccupate / maxOreTeam * 100))
        : 0;

      const nessunOperativo = numOperativiTotali === 0;

      return {
        ...sett,
        perUtente: Object.values(perUtente),
        oreOccupate: oreTotaleOccupate,
        maxOreTeam,
        percentualeOccupata,
        oreImpegnatePiano,
        oreReali,
        numOperativiTotali,
      };
    });
  },[settimane, programmazioniRaw, utenti, projects, commessePreesistenti, dipendenti, manutenzionePiani]);

  // Simulatore disponibilità
  const [simOperai, setSimOperai] = useState(1);
  const [simGiorni, setSimGiorni] = useState(5);
  const [simLivello, setSimLivello] = useState(0); // 0 = qualsiasi

  // Helper: trova livello operaio — ora già incluso in perUtente.livello
  const getLivelloOperaio = (uid) => {
    // Prima cerca in perUtente (ha il livello già calcolato)
    for (const sett of analisiSettimane) {
      const u = sett.perUtente.find(u => u.uid === uid);
      if (u) return u.livello || 0;
    }
    // Fallback: cerca in anagrafica dipendenti
    const dip = dipendenti.find(d => d.nexoraUid === uid);
    return parseInt(dip?.livelloProfessionalita)||0;
  };

  // primaFinestra: prima settimana con abbastanza operai liberi per i parametri richiesti
  // Usa perUtente (chi ha account Nexiora) per la ricerca di disponibilità specifica
  const primaFinestra = useMemo(()=>{
    for (const sett of analisiSettimane) {
      const disponibili = sett.perUtente.filter(u => {
        // Verifica giorni liberi sufficienti
        if (u.giorniOccupati > (5 - simGiorni)) return false;
        // Verifica livello minimo
        if (simLivello > 0 && u.livello < simLivello) return false;
        return true;
      });
      if (disponibili.length >= simOperai) {
        return { sett, operai: disponibili.slice(0, simOperai) };
      }
    }
    return null;
  },[analisiSettimane, simOperai, simGiorni, simLivello]);

  // Stato operativi per UI (calcolato direttamente sui dipendenti caricati)
  const nessunOperativo = dipendenti.filter(d => d.ruolo === "Operativo").length === 0;
  const operativiSenzaAccount = dipendenti.filter(d => d.ruolo === "Operativo" && !d.nexoraUid).length;

  const [settCorrente, setSettCorrente] = useState(0);
  const settVista = analisiSettimane[settCorrente];
  const [activeTab, setActiveTab] = useState("pianificazione");
  const [onNavigateCb, setOnNavigateCb] = useState(null);
  const [simModal, setSimModal] = useState(false);
  const [simForm, setSimForm] = useState({ cliente:"", codiceCommessa:"", nome:"", dataInizio:"", durataDays:10, oreUomo:80, livello:0, priorita:"normale" });
  const [simResult, setSimResult] = useState(null);

  // ── Indice affidabilità simulazione ────────────────────────────────────────
  const indiceAffidabilita = useMemo(()=>{
    let score = 100;
    const note = [];

    const operativiTotali = dipendenti.filter(d => d.ruolo === "Operativo").length;
    const operativiInNexiora = dipendenti.filter(d => d.ruolo === "Operativo" && d.nexoraUid).length;

    // BLOCCO CRITICO: nessun operativo in anagrafica
    // Questo è il prerequisito di tutto — senza operativi nulla funziona
    if (operativiTotali === 0) {
      return {
        score: 0,
        livello: "non calcolabile",
        colore: "#dc2626",
        emoji: "🔴",
        note: ["Nessun dipendente con categoria operativa Operativo in anagrafica — impossibile calcolare l'affidabilità. Vai in Dipendenti e imposta la categoria operativa per i tuoi operai."],
        bloccante: true,
      };
    }

    // Operativi collegati a Nexiora?
    // Penalizza solo se NESSUNO è collegato (non se alcuni mancano)
    if (operativiInNexiora === 0) {
      score -= 25;
      note.push(`Nessun operaio ancora collegato a un account Nexiora — la programmazione settimanale non può essere compilata. Collega gli operai dalla colonna "Collega account Nexiora" in Dipendenti.`);
    } else if (operativiInNexiora < operativiTotali) {
      score -= 10;
      note.push(`${operativiTotali - operativiInNexiora} operaio/i su ${operativiTotali} non ancora collegati a Nexiora — programmazione parziale.`);
    }

    // Programmazione compilata? (solo se ci sono operativi in Nexiora)
    if (operativiInNexiora > 0) {
      const settConDati = analisiSettimane.filter(s => s.oreReali > 0).length;
      if (settConDati === 0) {
        score -= 20;
        note.push("Programmazione settimanale non ancora compilata — compila almeno le prossime 4 settimane per calcoli più precisi.");
      } else if (settConDati < 4) {
        score -= 10;
        note.push(`Programmazione compilata solo per ${settConDati} settimana/e — compila almeno 4 settimane per aumentare la precisione.`);
      }
    }

    // Dipendenti con categoria operativa non impostata?
    const dipSenzaRuolo = dipendenti.filter(d => !d.ruolo).length;
    if (dipSenzaRuolo > 0) {
      score -= Math.min(15, 5 * dipSenzaRuolo);
      note.push(`${dipSenzaRuolo} dipendente/i senza categoria operativa impostata — potrebbero essere esclusi dal calcolo.`);
    }

    // Piano di carico commesse compilato?
    const commesseAttive = projects.filter(p => !p.isParent && !p.archived);
    const commesseSenzaPiano = commesseAttive.filter(p =>
      !p.dataInizio || !p.dataFine || (!p.operaiNecessari && (!p.squadra || p.squadra.length === 0))
    ).length;
    if (commesseAttive.length > 0 && commesseSenzaPiano > 0) {
      score -= Math.min(20, 5 * commesseSenzaPiano);
      note.push(`${commesseSenzaPiano} commessa/e attiva/e senza piano di carico (date + operai) — il calcolo disponibilità per queste commesse è incompleto.`);
    }

    // Livello professionalità degli operativi
    const dipSenzaLivello = dipendenti.filter(d => d.ruolo === "Operativo" && !d.livelloProfessionalita).length;
    if (dipSenzaLivello > 0) {
      score -= 5;
      note.push(`${dipSenzaLivello} operaio/i senza livello professionalità — il simulatore non potrà filtrare per qualifica.`);
    }

    // Nota informativa sulle preesistenti (non penalizza — è opzionale)
    if (commessePreesistenti.length === 0 && commesseAttive.length > 0) {
      note.push("Suggerimento: se hai commesse già in corso non ancora inserite in Nexiora, aggiungile come 'Commesse preesistenti' per un calcolo più preciso.");
    }

    score = Math.max(0, Math.min(100, score));
    const livello = score >= 80 ? "alta" : score >= 50 ? "media" : "bassa";
    const colore = score >= 80 ? "#059669" : score >= 50 ? "#d97706" : "#dc2626";
    const emoji = score >= 80 ? "🟢" : score >= 50 ? "🟡" : "🔴";
    return { score, livello, colore, emoji, note, bloccante: false };
  },[analisiSettimane, dipendenti, projects, commessePreesistenti]);

  // ── Capacità produttiva per periodo ─────────────────────────────────────────
  const capacitaPeriodi = useMemo(()=>{
    const periodi = [30,60,90,180];
    return periodi.map(giorni=>{
      const fine = addDays(oggi, giorni);
      let oreDisponibili=0, oreOccupate=0;
      analisiSettimane.forEach(sett=>{
        if(sett.lunedi>fine) return;
        oreDisponibili += sett.maxOreTeam;
        oreOccupate += sett.oreOccupate;
      });
      const pct = oreDisponibili>0?Math.round((oreOccupate/oreDisponibili)*100):0;
      return { giorni, oreDisponibili, oreOccupate, oreLibere:oreDisponibili-oreOccupate, pct };
    });
  },[analisiSettimane, oggi]);

  // ── Funzione simulazione nuova commessa ──────────────────────────────────────
  const eseguiSimulazione = () => {
    const { durataDays, oreUomo, livello, dataInizio } = simForm;
    const giorni = parseInt(durataDays)||10;
    const ore = parseInt(oreUomo)||80;
    const operaiNecessari = Math.ceil(ore/(giorni*8));

    // Trova prima finestra disponibile con i parametri simulati
    let finestraSimulata = null;
    for(const sett of analisiSettimane){
      if(dataInizio && sett.lunedi < new Date(dataInizio+"T00:00:00")) continue;
      const disponibili = sett.perUtente.filter(u=>{
        if(u.giorniOccupati>=(5)) return false;
        if(livello>0 && u.livello<livello) return false;
        return true;
      });
      if(disponibili.length>=operaiNecessari){
        finestraSimulata = { sett, operai:disponibili.slice(0,operaiNecessari) };
        break;
      }
    }

    // Calcola saturazione prevista durante la commessa
    const settImpattate = analisiSettimane.filter(s=>{
      if(finestraSimulata && s.lunedi < finestraSimulata.sett.lunedi) return false;
      return true;
    }).slice(0, Math.ceil(giorni/5));

    const satMediaAttuale = settImpattate.length>0
      ? Math.round(settImpattate.reduce((a,s)=>a+s.percentualeOccupata,0)/settImpattate.length)
      : 0;
    const oreAggiuntive = operaiNecessari*giorni*8;
    const maxOre = settImpattate.reduce((a,s)=>a+s.maxOreTeam,0)||1;
    const satPrevista = Math.min(100, satMediaAttuale + Math.round((oreAggiuntive/maxOre)*100));

    // Conflitti
    const numOperativiReali = dipendenti.filter(d=>d.ruolo==="Operativo").length;
    const conflitti = [];
    if(satPrevista>90) conflitti.push("Saturazione team superiore al 90% — rischio sovraccarico");
    if(!finestraSimulata) conflitti.push("Nessuna finestra disponibile nelle prossime 8 settimane con i parametri indicati");
    if(operaiNecessari > numOperativiReali)
      conflitti.push(`Servono ${operaiNecessari} operai ma ne hai ${numOperativiReali} in organico`);
    if(numOperativiReali === 0)
      conflitti.push("Nessun dipendente con categoria operativa Operativo in anagrafica — impossibile calcolare la disponibilità");

    // Suggerimenti
    const suggerimenti = [];
    if(satPrevista>90) suggerimenti.push("Considera di posticipare la data di avvio");
    if(satPrevista>90) suggerimenti.push("Valuta l'utilizzo di personale esterno o subappalto");
    if(conflitti.length===0 && satPrevista>70) suggerimenti.push("Monitora settimanalmente l'avanzamento per evitare ritardi");
    if(!finestraSimulata) suggerimenti.push("Nessuna risorsa disponibile — valuta ridistribuzione carico o suddivisione in fasi");

    setSimResult({ finestraSimulata, operaiNecessari, satMediaAttuale, satPrevista, conflitti, suggerimenti, oreAggiuntive, capacitaResidua:100-satPrevista });
  };

  return (
    <div style={{display:"flex",flexDirection:"column",height:"100%",overflow:"hidden"}}>
      {/* Header */}
      <div style={{background:"#fff",borderBottom:"1px solid #e5e7eb",padding:"14px 20px 0",flexShrink:0}}>
        <div style={{marginBottom:12}}>
          <h2 style={{fontSize:16,fontWeight:800,display:"flex",alignItems:"center",gap:8}}>
            <BarChart2 size={17} color="#7c3aed"/> Capacità Produttiva {piano&&<BadgePiano funzione="pianificazione" piano={piano} giorniRimasti={giorniRimasti}/>}
            <button onClick={()=>setSimModal(true)}
              style={{padding:"4px 12px",background:"#7c3aed",border:"none",borderRadius:8,color:"#fff",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif",display:"flex",alignItems:"center",gap:6}}>
              ▶ Simula Nuova Commessa
            </button>
            <InsightButton onNavigate={onNavigate}/>
          </h2>
        </div>
        {/* Radar noleggi attivi — colpo d'occhio, calcolato dal Registro Noleggi */}
        {radarNoleggi.length > 0 && (
          <div style={{display:"flex",flexWrap:"wrap",gap:8,marginBottom:12}}>
            {radarNoleggi.map(r=>{
              const t = TIPI_MEZZO_OPERA[r.tipo] || TIPI_VEICOLO[r.tipo] || {label:r.tipo,emoji:"📦"};
              return (
                <div key={r.tipo} title={r.commesse.join(", ")||"Generico aziendale"}
                  onClick={()=>onNavigate&&onNavigate({page:"registro-noleggi"})}
                  style={{display:"flex",alignItems:"center",gap:6,background:"#fff7ed",border:"1px solid #fed7aa",borderRadius:20,padding:"5px 12px",fontSize:11.5,fontWeight:700,color:"#9a3412",cursor:onNavigate?"pointer":"default"}}>
                  <span>{t.emoji}</span>
                  <span>{r.count} {t.label.toLowerCase()}{r.count>1?(t.label.endsWith("a")?"e":"i"):""} attiv{r.count>1?"i":"o"}</span>
                  {r.commesse.length>0 && <span style={{color:"#c2712d",fontWeight:500}}>· {r.commesse.slice(0,2).join(", ")}{r.commesse.length>2?" +altre":""}</span>}
                </div>
              );
            })}
          </div>
        )}
        {/* Tab */}
        <div style={{display:"flex",gap:0}}>
          {[
            {id:"pianificazione", label:"📅 Pianificazione"},
            {id:"capacita",       label:"📊 Riepilogo"},
            {id:"fattibilita",    label:"🔍 Analisi Fattibilità"},
            {id:"affidabilita",   label:"🎯 Affidabilità"},
          ].map(t=>(
            <button key={t.id} onClick={()=>setActiveTab(t.id)}
              style={{padding:"8px 16px",border:"none",borderBottom:activeTab===t.id?"3px solid #7c3aed":"3px solid transparent",background:"none",color:activeTab===t.id?"#7c3aed":"#6b7280",fontWeight:activeTab===t.id?700:400,cursor:"pointer",fontSize:12,fontFamily:"'Inter',sans-serif",whiteSpace:"nowrap"}}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Contenuto tab */}
      <div style={{flex:1,overflowY:"auto",display:"flex",flexDirection:"column"}}>

      {/* ══ TAB PIANIFICAZIONE ══ */}
      {activeTab==="pianificazione" && <div style={{display:"flex",flexDirection:"column",flex:1}}>

        {/* Banner nessun operativo nel tab Pianificazione */}
        {nessunOperativo && (
          <div style={{margin:"12px 16px 0",background:"#fef2f2",border:"2px solid #dc2626",borderRadius:10,padding:"12px 16px",display:"flex",gap:12,alignItems:"flex-start"}}>
            <span style={{fontSize:20,flexShrink:0}}>🚫</span>
            <div>
              <div style={{fontSize:13,fontWeight:800,color:"#dc2626",marginBottom:2}}>Nessun operativo registrato</div>
              <div style={{fontSize:11,color:"#991b1b",lineHeight:1.5}}>
                La griglia è vuota perché non risultano dipendenti con categoria operativa <strong>Operativo</strong>. Vai in <strong>Dipendenti</strong> e imposta la categoria operativa per i tuoi operai. Il simulatore di disponibilità non può funzionare senza questa informazione.
              </div>
              {canSee("dipendenti") && onNavigate && (
                <button onClick={()=>onNavigate({page:"dipendenti"})}
                  style={{marginTop:8,padding:"6px 14px",background:"#dc2626",color:"#fff",border:"none",borderRadius:7,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                  Vai a Dipendenti →
                </button>
              )}
            </div>
          </div>
        )}

        {/* Simulatore */}
        <div style={{padding:"16px 20px 0",background:"#fff",flexShrink:0}}>
        <div style={{background:"linear-gradient(135deg,#1e1b4b,#3730a3)",borderRadius:12,padding:"16px 20px",marginBottom:12}}>
          <div style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,.5)",textTransform:"uppercase",letterSpacing:".06em",marginBottom:10}}>
            📊 Calcolo disponibilità maestranze per nuova commessa
          </div>
          <div style={{display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
            <span style={{fontSize:13,color:"rgba(255,255,255,.8)"}}>Ho bisogno di</span>
            <div style={{display:"flex",alignItems:"center",gap:6,background:"rgba(255,255,255,.1)",borderRadius:8,padding:"4px 10px"}}>
              <button onClick={()=>setSimOperai(n=>Math.max(1,n-1))} style={{background:"none",border:"none",color:"#fff",cursor:"pointer",fontSize:16,fontWeight:700,padding:"0 4px"}}>−</button>
              <input type="number" min={1} value={simOperai}
                onChange={e=>{ const v=e.target.value; setSimOperai(v===""?1:Math.max(1,parseInt(v)||1)); }}
                onFocus={e=>e.target.select()}
                style={{width:36,textAlign:"center",background:"rgba(255,255,255,.15)",border:"1px solid rgba(255,255,255,.3)",borderRadius:6,color:"#fff",fontWeight:800,fontSize:15,fontFamily:"'Inter',sans-serif",outline:"none",padding:"2px 0"}}/>
              <button onClick={()=>setSimOperai(n=>n+1)} style={{background:"none",border:"none",color:"#fff",cursor:"pointer",fontSize:16,fontWeight:700,padding:"0 4px"}}>+</button>
            </div>
            <span style={{fontSize:13,color:"rgba(255,255,255,.8)"}}>operai per</span>
            <div style={{display:"flex",alignItems:"center",gap:6,background:"rgba(255,255,255,.1)",borderRadius:8,padding:"4px 10px"}}>
              <button onClick={()=>setSimGiorni(n=>Math.max(1,n-1))} style={{background:"none",border:"none",color:"#fff",cursor:"pointer",fontSize:16,fontWeight:700,padding:"0 4px"}}>−</button>
              <input type="number" min={1} value={simGiorni}
                onChange={e=>{ const v=e.target.value; setSimGiorni(v===""?1:Math.max(1,parseInt(v)||1)); }}
                onFocus={e=>e.target.select()}
                style={{width:36,textAlign:"center",background:"rgba(255,255,255,.15)",border:"1px solid rgba(255,255,255,.3)",borderRadius:6,color:"#fff",fontWeight:800,fontSize:15,fontFamily:"'Inter',sans-serif",outline:"none",padding:"2px 0"}}/>
              <button onClick={()=>setSimGiorni(n=>n+1)} style={{background:"none",border:"none",color:"#fff",cursor:"pointer",fontSize:16,fontWeight:700,padding:"0 4px"}}>+</button>
            </div>
            <span style={{fontSize:13,color:"rgba(255,255,255,.8)"}}>giorni</span>
            <span style={{fontSize:13,color:"rgba(255,255,255,.8)"}}>livello min.</span>
            <select value={simLivello} onChange={e=>setSimLivello(parseInt(e.target.value))}
              style={{background:"rgba(255,255,255,.1)",border:"none",borderRadius:8,padding:"6px 10px",color:"#fff",fontSize:12,fontFamily:"'Inter',sans-serif",outline:"none"}}>
              <option value={0} style={{color:"#111"}}>Qualsiasi</option>
              <option value={1} style={{color:"#111"}}>1 - Inesperto+</option>
              <option value={2} style={{color:"#111"}}>2 - Poco esperto+</option>
              <option value={3} style={{color:"#111"}}>3 - Autonomo+</option>
              <option value={4} style={{color:"#111"}}>4 - Esperto+</option>
              <option value={5} style={{color:"#111"}}>5 - Solo molto esperti</option>
            </select>
            <span style={{fontSize:13,color:"rgba(255,255,255,.8)"}}>→</span>
            {primaFinestra ? (
              <button onClick={()=>setSettCorrente(analisiSettimane.indexOf(primaFinestra.sett))}
                title="Vai a questa settimana nel dettaglio sotto"
                style={{background:"#059669",borderRadius:8,padding:"6px 14px",border:"none",cursor:"pointer",textAlign:"left",fontFamily:"'Inter',sans-serif"}}>
                <div style={{fontSize:11,color:"rgba(255,255,255,.7)"}}>Prima finestra disponibile — clicca per vederla ↓</div>
                <div style={{fontSize:13,fontWeight:800,color:"#fff"}}>
                  Settimana {fmtData(primaFinestra.sett.lunedi)} con {primaFinestra.operai.map(o=>o.nome.split(" ")[0]).join(", ")}
                </div>
              </button>
            ) : (
              <div style={{background:"#dc2626",borderRadius:8,padding:"6px 14px"}}>
                <div style={{fontSize:13,fontWeight:700,color:"#fff"}}>Nessuna finestra disponibile nelle prossime 8 settimane</div>
              </div>
            )}
          </div>
          <div style={{fontSize:10,color:"rgba(255,255,255,.3)",marginTop:8}}>
            ℹ️ Basato sulla programmazione settimanale compilata. Compila la programmazione in anticipo per risultati più precisi.
          </div>
        </div>

        </div>{/* fine padding simulatore */}

        {/* Navigazione settimane + stato settimana, affiancati: a sinistra scegli la
            settimana, a destra il suo stato — sempre visibili insieme, anche mentre
            scorri la tabella sotto (prima il blocco di stato era dentro l'area che scorre). */}
        <div style={{padding:"0 20px 12px",background:"#fff",flexShrink:0,display:"flex",gap:12,alignItems:"stretch"}}>
          <div style={{flex:"1 1 56%",display:"flex",alignItems:"center",gap:8,background:"#f9fafb",border:"1px solid #e5e7eb",borderRadius:12,padding:"10px 12px"}}>
          <button onClick={()=>setSettCorrente(s=>Math.max(0,s-1))} disabled={settCorrente===0}
            style={{border:"1px solid #e5e7eb",background:"#fff",borderRadius:6,padding:"4px 8px",cursor:settCorrente===0?"not-allowed":"pointer",opacity:settCorrente===0?.4:1}}>
            <ChevronLeft size={14}/>
          </button>
          <div style={{display:"flex",gap:6,overflowX:"auto",flex:1}}>
            {analisiSettimane.map((s,i)=>{
              const pct = s.percentualeOccupata;
              const col = pct>=90?"#dc2626":pct>=60?"#f59e0b":"#059669";
              return (
                <button key={i} onClick={()=>setSettCorrente(i)}
                  style={{flexShrink:0,padding:"4px 10px",borderRadius:8,border:`2px solid ${settCorrente===i?col:"#e5e7eb"}`,background:settCorrente===i?col+"11":"#fff",cursor:"pointer",textAlign:"center",fontFamily:"'Inter',sans-serif"}}>
                  <div style={{fontSize:9,color:"#6b7280",fontWeight:600}}>{fmtData(s.lunedi)}</div>
                  <div style={{fontSize:11,fontWeight:800,color:col}}>{pct}%</div>
                </button>
              );
            })}
          </div>
          <button onClick={()=>setSettCorrente(s=>Math.min(7,s+1))} disabled={settCorrente===7}
            style={{border:"1px solid #e5e7eb",background:"#fff",borderRadius:6,padding:"4px 8px",cursor:settCorrente===7?"not-allowed":"pointer",opacity:settCorrente===7?.4:1}}>
            <ChevronRight size={14}/>
          </button>
        </div>

          {/* Stato della settimana selezionata — stesso blocco di prima, solo spostato qui accanto */}
          {settVista && (
            <div style={{
              flex:"1 1 44%",
              background: settVista.percentualeOccupata>=90 ? "#fee2e2" : settVista.percentualeOccupata>=70 ? "#fff7ed" : "#fff",
              border: settVista.percentualeOccupata>=90 ? "2px solid #dc2626" : settVista.percentualeOccupata>=70 ? "1px solid #fed7aa" : "1px solid #e5e7eb",
              borderRadius:12, padding:"10px 14px"
            }}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:6}}>
                <h3 style={{fontSize:13,fontWeight:700,color:"#111827",whiteSpace:"nowrap"}}>
                  Settimana {settVista.label}
                </h3>
                {settVista.maxOreTeam > 0 ? (
                  <div style={{background: settVista.percentualeOccupata>=90?"#fff":settVista.percentualeOccupata>=60?"#fff":"#d1fae5", color: settVista.percentualeOccupata>=90?"#dc2626":settVista.percentualeOccupata>=60?"#d97706":"#059669", padding:"3px 10px",borderRadius:10,fontSize:11,fontWeight:700,whiteSpace:"nowrap"}}>
                    {settVista.percentualeOccupata>=90?"🔴 Saturata":settVista.percentualeOccupata>=60?"🟡 Quasi piena":"🟢 Disponibile"}
                  </div>
                ) : (
                  <div style={{background:"#fef2f2",color:"#dc2626",padding:"3px 10px",borderRadius:10,fontSize:11,fontWeight:700,whiteSpace:"nowrap"}}>
                    🚫 Nessun operativo
                  </div>
                )}
              </div>
              <div style={{fontSize:11,color:"#6b7280",marginTop:3}}>
                {settVista.numOperativiTotali > 0 && (
                  <span>
                    <strong style={{color:"#374151"}}>{settVista.numOperativiTotali}</strong> operativi in organico
                    {operativiSenzaAccount > 0 && (
                      <span style={{color:"#d97706",marginLeft:4}}>
                        ({operativiSenzaAccount} senza account Nexiora)
                      </span>
                    )}
                    {settVista.maxOreTeam > 0 && " · "}
                  </span>
                )}
                {settVista.maxOreTeam > 0 && (
                  <span>Ore occupate: <strong style={{color:"#374151"}}>{settVista.oreOccupate}h</strong> / {settVista.maxOreTeam}h</span>
                )}
              </div>
              {settVista.percentualeOccupata>=90 && (
                <div style={{fontSize:11,color:"#991b1b",marginTop:6,paddingTop:6,borderTop:"1px solid #fca5a5"}}>
                  ⚠️ Il team è quasi completamente impegnato. Rischio di sovraccarico.
                </div>
              )}
              {settVista.percentualeOccupata>=70 && settVista.percentualeOccupata<90 && (
                <div style={{fontSize:11,color:"#9a3412",marginTop:6,paddingTop:6,borderTop:"1px solid #fed7aa"}}>
                  ⚠️ Capacità residua limitata, valuta con attenzione.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Griglia settimana */}
        <div style={{flex:1,overflowY:"auto",padding:"0 16px 16px"}}>
        {settVista && (
          <>
            <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:12,overflow:"hidden",marginTop:16}}>
              {/* Header giorni */}
              <div style={{display:"grid",gridTemplateColumns:"160px repeat(7, 1fr)",background:"#1e1b4b"}}>
                <div style={{padding:"8px 12px",fontSize:10,fontWeight:700,color:"rgba(255,255,255,.5)"}}>Operaio</div>
                {settVista.giorni.map((g,gi)=>{
                  const isSabDom = g.getDay()===0||g.getDay()===6;
                  return (
                    <div key={gi} style={{padding:"8px 6px",textAlign:"center",fontSize:9,fontWeight:700,color:isSabDom?"rgba(255,255,255,.3)":"rgba(255,255,255,.8)"}}>
                      <div>{GIORNI[gi]}</div>
                      <div style={{fontSize:11,marginTop:1}}>{fmtData(g)}</div>
                    </div>
                  );
                })}
              </div>

              {/* Righe operai */}
              {settVista.perUtente.map((u,ri)=>{
                const pctU = Math.round(u.giorniOccupati/5*100);
                return (
                  <div key={u.uid} style={{display:"grid",gridTemplateColumns:"160px repeat(7, 1fr)",borderBottom:ri<settVista.perUtente.length-1?"1px solid #f3f4f6":"none",background:ri%2===0?"#fff":"#fafafa"}}>
                    {/* Nome operaio */}
                    <div style={{padding:"8px 12px",display:"flex",alignItems:"center",gap:8,borderRight:"1px solid #f3f4f6"}}>
                      <div style={{width:28,height:28,borderRadius:"50%",background:"#ede9fe",display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:700,color:"#7c3aed",flexShrink:0}}>
                        {(u.nome||"?").split(" ").map(s=>s[0]).join("").slice(0,2).toUpperCase()}
                      </div>
                      <div>
                        <div style={{fontSize:11,fontWeight:600,color:"#374151"}}>{u.nome.split(" ")[0]}</div>
                        <div style={{display:"flex",alignItems:"center",gap:4,marginTop:1}}>
                          {(()=>{const lv=getLivelloOperaio(u.uid);const li=LIVELLI_PROF[lv];return lv>0&&li?<span title={`Livello professionale: ${lv} - ${li.label}`} style={{fontSize:8,fontWeight:700,color:li.col,background:li.col+"18",padding:"1px 5px",borderRadius:4,whiteSpace:"nowrap",cursor:"help"}}>{lv} - {li.label}</span>:null;})()}
                          <span style={{fontSize:9,color:pctU>=80?"#dc2626":pctU>=40?"#d97706":"#059669",fontWeight:700}}>{5-u.giorniOccupati} gg liberi</span>
                        </div>
                      </div>
                    </div>
                    {/* Celle giorni */}
                    {settVista.giorni.map((g,gi)=>{
                      const ds = `${g.getFullYear()}-${String(g.getMonth()+1).padStart(2,"0")}-${String(g.getDate()).padStart(2,"0")}`;
                      const cella = u.giorni[ds];
                      const isSabDom = g.getDay()===0||g.getDay()===6;
                      const colore = cella ? (coloreCommessa[cella.commessaNome]||"#7c3aed") : null;
                      return (
                        <div key={gi} style={{padding:"4px 3px",display:"flex",alignItems:"center",justifyContent:"center",background:isSabDom?"#f9fafb":"transparent",borderLeft:"1px solid #f3f4f6",minHeight:48}}>
                          {cella ? (
                            <div style={{background:cella.stimato?`repeating-linear-gradient(45deg,${colore}11,${colore}11 4px,transparent 4px,transparent 8px)`:colore+"22",border:`1px solid ${colore}${cella.stimato?"33":"55"}`,borderRadius:5,padding:"3px 5px",textAlign:"center",width:"100%",opacity:cella.stimato?.8:1}}>
                              <div style={{fontSize:8,fontWeight:700,color:colore,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{cella.commessaNome.slice(0,12)}</div>
                              {cella.stimato && <div style={{fontSize:7,color:colore,opacity:.6}}>~stimato</div>}
                              {!cella.stimato && cella.ore && <div style={{fontSize:8,color:colore,opacity:.7}}>{cella.ore}h</div>}
                            </div>
                          ) : isSabDom ? (
                            <span style={{fontSize:9,color:"#d1d5db"}}>—</span>
                          ) : (
                            <div style={{width:10,height:10,borderRadius:"50%",background:"#d1fae5",border:"2px solid #059669"}} title="Disponibile"/>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>

            {/* Guida alla lettura — un unico riquadro: prima erano due box separati
                (legenda simboli + fonti dati) che spiegavano due aspetti della stessa cosa. */}
            <div style={{marginTop:14,background:"#f9fafb",border:"1px solid #e5e7eb",borderRadius:10,padding:"12px 16px"}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                <div style={{fontSize:11,fontWeight:700,color:"#374151",textTransform:"uppercase",letterSpacing:".05em"}}>📖 Guida alla lettura</div>
                {infoFontiAperto && (
                  <button onClick={()=>{setInfoFontiAperto(false);localStorage.setItem("nexiora_infoFontiChiuso","true");}}
                    title="Comprimi — puoi riaprirla quando vuoi"
                    style={{background:"none",border:"none",cursor:"pointer",color:"#9ca3af",fontSize:11,padding:0}}>
                    ✕ Comprimi
                  </button>
                )}
              </div>
              <div style={{display:"flex",gap:16,flexWrap:"wrap",fontSize:11,color:"#6b7280",marginBottom:infoFontiAperto?10:0,paddingBottom:infoFontiAperto?10:0,borderBottom:infoFontiAperto?"1px solid #e5e7eb":"none"}}>
                <span style={{display:"flex",alignItems:"center",gap:4}}><div style={{width:10,height:10,borderRadius:"50%",background:"#d1fae5",border:"2px solid #059669"}}/> Disponibile</span>
                <span style={{display:"flex",alignItems:"center",gap:4}}><div style={{width:10,height:10,borderRadius:3,background:"#ede9fe",border:"1px solid #7c3aed"}}/> Occupato</span>
                <span style={{display:"flex",alignItems:"center",gap:4}}><div style={{width:14,height:10,background:"repeating-linear-gradient(45deg,#7c3aed11,#7c3aed11 3px,transparent 3px,transparent 6px)",border:"1px solid #7c3aed33",borderRadius:2}}/> Stimato (da piano di carico)</span>
              </div>

              {infoFontiAperto ? (
                <div>
                  <div style={{fontSize:11,fontWeight:700,color:"#374151",marginBottom:6}}>Il calcolo si basa su tre fonti:</div>
                  <div style={{display:"flex",flexDirection:"column",gap:4,fontSize:11,color:"#6b7280"}}>
                    <span>📅 <strong style={{color:"#374151"}}>Programmazione settimanale</strong> — ore reali già inserite (barre solide)</span>
                    <span>📋 <strong style={{color:"#374151"}}>Piano di carico commesse Nexiora</strong> — stima da data inizio/fine e operai necessari (barre tratteggiate)</span>
                    <span>⚠️ <strong style={{color:"#374151"}}>Commesse preesistenti</strong> — commesse già in corso non ancora in Nexiora, inserite nella sezione qui sotto</span>
                  </div>
                  <div style={{fontSize:10,color:"#9ca3af",marginTop:6,fontStyle:"italic"}}>
                    Solo i dipendenti con categoria operativa <strong>Operativo</strong> vengono considerati nel calcolo.
                  </div>
                </div>
              ) : (
                <button onClick={()=>{setInfoFontiAperto(true);localStorage.removeItem("nexiora_infoFontiChiuso");}}
                  style={{background:"none",border:"1px dashed #d1d5db",borderRadius:8,padding:"5px 12px",cursor:"pointer",color:"#6b7280",fontSize:11,fontWeight:600}}>
                  📊 Come si calcola? →
                </button>
              )}
            </div>
          </>
        )}
        </div>{/* fine griglia */}
      </div>}{/* fine tab pianificazione */}

      {/* ══ TAB CAPACITÀ PRODUTTIVA ══ */}
      {activeTab==="capacita" && (
        <div style={{padding:24}}>
          <h3 style={{fontSize:15,fontWeight:800,marginBottom:4,color:"#111827"}}>📊 Capacità Produttiva Aziendale</h3>
          <p style={{fontSize:12,color:"#9ca3af",marginBottom:16}}>Basata sul piano di carico delle commesse attive e sulla programmazione settimanale compilata.</p>

          {/* Banner critico: nessun operativo registrato */}
          {nessunOperativo && (
            <div style={{background:"#fef2f2",border:"2px solid #dc2626",borderRadius:12,padding:"16px 20px",marginBottom:20,display:"flex",gap:14,alignItems:"flex-start"}}>
              <span style={{fontSize:24,flexShrink:0}}>🚫</span>
              <div>
                <div style={{fontSize:14,fontWeight:800,color:"#dc2626",marginBottom:4}}>
                  Dati non disponibili — nessun dipendente Operativo registrato
                </div>
                <div style={{fontSize:12,color:"#991b1b",lineHeight:1.6}}>
                  Per calcolare la capacità produttiva, Nexiora ha bisogno di conoscere quanti operai hai in organico.<br/>
                  <strong>Cosa fare:</strong> vai in <strong>Dipendenti</strong>, inserisci i tuoi operai e imposta il campo <strong>Categoria operativa = Operativo</strong> per ognuno di loro.<br/>
                  Finché non ci sono operativi registrati, i dati di saturazione e disponibilità non sono attendibili e non vengono mostrati.
                </div>
                {canSee("dipendenti") && onNavigate && (
                  <button onClick={()=>onNavigate({page:"dipendenti"})}
                    style={{marginTop:8,padding:"6px 14px",background:"#dc2626",color:"#fff",border:"none",borderRadius:7,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                    Vai a Dipendenti →
                  </button>
                )}
              </div>
            </div>
          )}
          {!nessunOperativo && operativiSenzaAccount > 0 && (
            <div style={{background:"#fff7ed",border:"1px solid #fed7aa",borderRadius:10,padding:"12px 16px",marginBottom:16,display:"flex",gap:12,alignItems:"flex-start"}}>
              <span style={{fontSize:18,flexShrink:0}}>⚠️</span>
              <div style={{fontSize:12,color:"#92400e",lineHeight:1.6}}>
                <strong>{operativiSenzaAccount} operaio/i</strong> hanno categoria operativa Operativo ma non sono ancora collegati a un account Nexiora.<br/>
                Il calcolo della programmazione reale sarà parziale. Collega gli operai dalla sezione Dipendenti → campo "Collega account Nexiora".
              </div>
            </div>
          )}

          {/* Alert critici — i più importanti per chi pianifica */}
          {(()=>{
            const alerts = [];
            // Saturazione critica nelle prossime 2 settimane
            const settCritiche = analisiSettimane.slice(0,2).filter(s=>s.percentualeOccupata>=90);
            if(settCritiche.length>0) alerts.push({
              tipo:"critico", emoji:"🔴",
              titolo:"Saturazione critica imminente",
              testo:`${settCritiche.length} delle prossime 2 settimane superano il 90% di saturazione. Rischio di non poter accettare nuovi impegni.`
            });
            // Saturazione alta nelle prossime 4 settimane
            const settAlte = analisiSettimane.slice(0,4).filter(s=>s.percentualeOccupata>=70&&s.percentualeOccupata<90);
            if(settAlte.length>=2) alerts.push({
              tipo:"alto", emoji:"🟠",
              titolo:"Carico elevato nelle prossime settimane",
              testo:`${settAlte.length} settimane con saturazione tra 70% e 90%. Valuta bene prima di assumere nuovi impegni.`
            });
            // Commesse senza piano di carico
            const senzaPiano = projects.filter(p=>!p.isParent&&!p.archived&&(!p.dataInizio||!p.dataFine||!p.operaiNecessari));
            if(senzaPiano.length>0) alerts.push({
              tipo:"warning", emoji:"⚠️",
              titolo:`${senzaPiano.length} commessa/e senza dati di carico`,
              testo:`${senzaPiano.map(p=>p.name).join(", ")} — il loro impatto non è incluso nel calcolo. Le analisi potrebbero essere sottostimate.`
            });
            // Nessun operaio collegato
            const operativiInNexiora = dipendenti.filter(d=>d.ruolo==="Operativo"&&d.nexoraUid).length;
            if(operativiInNexiora===0&&dipendenti.filter(d=>d.ruolo==="Operativo").length>0) alerts.push({
              tipo:"info", emoji:"ℹ️",
              titolo:"Operativi non collegati a Nexiora",
              testo:"Il calcolo usa i dati del piano di carico. Per la griglia presenze e la programmazione dettagliata, collega gli operai a un account Nexiora."
            });
            // Tutto ok
            if(alerts.length===0) return (
              <div style={{background:"#d1fae5",border:"1px solid #6ee7b7",borderRadius:10,padding:"12px 16px",marginBottom:20,display:"flex",gap:10,alignItems:"center"}}>
                <span style={{fontSize:20}}>✅</span>
                <div>
                  <div style={{fontSize:13,fontWeight:700,color:"#059669"}}>Capacità produttiva nella norma</div>
                  <div style={{fontSize:11,color:"#065f46"}}>Nessuna criticità rilevata nelle prossime settimane.</div>
                </div>
              </div>
            );

            const STILI = {
              critico: {bg:"#fee2e2",border:"#fca5a5",col:"#dc2626"},
              alto:    {bg:"#fff7ed",border:"#fed7aa",col:"#ea580c"},
              warning: {bg:"#fef3c7",border:"#fde68a",col:"#d97706"},
              info:    {bg:"#dbeafe",border:"#93c5fd",col:"#2563eb"},
            };
            return (
              <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:20}}>
                {alerts.map((a,i)=>{
                  const st = STILI[a.tipo]||STILI.info;
                  return (
                    <div key={i} style={{background:st.bg,border:`1px solid ${st.border}`,borderRadius:10,padding:"12px 16px",display:"flex",gap:12,alignItems:"flex-start"}}>
                      <span style={{fontSize:20,flexShrink:0}}>{a.emoji}</span>
                      <div>
                        <div style={{fontSize:13,fontWeight:700,color:st.col,marginBottom:3}}>{a.titolo}</div>
                        <div style={{fontSize:12,color:"#374151",lineHeight:1.5}}>{a.testo}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
          {!nessunOperativo && <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))",gap:16,marginBottom:24}}>
            {capacitaPeriodi.map(({giorni,oreDisponibili,oreOccupate,oreLibere,pct})=>{
              const col = pct>=90?"#dc2626":pct>=60?"#d97706":"#059669";
              const bg = pct>=90?"#fee2e2":pct>=60?"#fef3c7":"#d1fae5";
              return (
                <div key={giorni} style={{background:"#fff",border:`2px solid ${col}22`,borderRadius:12,padding:20}}>
                  <div style={{fontSize:12,color:"#9ca3af",fontWeight:600,marginBottom:8}}>Prossimi {giorni} giorni</div>
                  <div style={{fontSize:32,fontWeight:800,color:col,marginBottom:4}}>{pct}%</div>
                  <div style={{fontSize:11,color:"#374151",marginBottom:12}}>saturazione prevista</div>
                  <div style={{background:"#f3f4f6",borderRadius:6,height:6,marginBottom:12}}>
                    <div style={{background:col,borderRadius:6,height:6,width:`${Math.min(100,pct)}%`}}/>
                  </div>
                  <div style={{display:"flex",justifyContent:"space-between",fontSize:11}}>
                    <span style={{color:"#6b7280"}}>Occupate: <strong>{oreOccupate}h</strong></span>
                    <span style={{color:col}}>Libere: <strong>{oreLibere}h</strong></span>
                  </div>
                  <div style={{marginTop:8,padding:"4px 8px",background:bg,borderRadius:6,fontSize:10,fontWeight:700,color:col,textAlign:"center"}}>
                    {pct>=90?"🔴 Periodo critico":pct>=60?"🟡 Quasi saturo":"🟢 Capacità disponibile"}
                  </div>
                </div>
              );
            })}
          </div>}
          {!nessunOperativo && <div style={{background:"#f8fafc",border:"1px solid #e5e7eb",borderRadius:12,padding:16}}>
            <div style={{fontSize:12,fontWeight:700,color:"#374151",marginBottom:8}}>ℹ️ Come leggere questi dati</div>
            <div style={{fontSize:11,color:"#6b7280",lineHeight:1.7}}>
              Sulla base dei dati attualmente presenti in Nexiora, questi valori rappresentano una stima della capacità produttiva del team operativo. La precisione dipende dalla completezza della programmazione settimanale e dei piani di carico delle commesse. Consulta la scheda Affidabilità Simulazione per capire quanto questi dati siano attendibili.
            </div>
          </div>}
        </div>
      )}

      {/* ══ TAB ANALISI FATTIBILITÀ ══ */}
      {activeTab==="fattibilita" && (
        <div style={{padding:24}}>
          <h3 style={{fontSize:15,fontWeight:800,marginBottom:4,color:"#111827"}}>🔍 Analisi di Fattibilità</h3>
          <p style={{fontSize:12,color:"#9ca3af",marginBottom:20}}>Analisi automatica basata sui dati presenti in Nexiora. Non richiede inserimenti aggiuntivi.</p>

          {/* Banner bloccante: nessun operativo */}
          {nessunOperativo && (
            <div style={{background:"#fef2f2",border:"2px solid #dc2626",borderRadius:12,padding:"16px 20px",marginBottom:20,display:"flex",gap:14,alignItems:"flex-start"}}>
              <span style={{fontSize:24,flexShrink:0}}>🚫</span>
              <div>
                <div style={{fontSize:14,fontWeight:800,color:"#dc2626",marginBottom:4}}>Analisi non disponibile — nessun operativo in anagrafica</div>
                <div style={{fontSize:12,color:"#991b1b",lineHeight:1.6}}>
                  Per eseguire l'analisi di fattibilità è necessario avere almeno un dipendente con categoria operativa <strong>Operativo</strong>.<br/>
                  Vai in <strong>Dipendenti</strong> → imposta il campo <strong>Categoria operativa = Operativo</strong> per i tuoi operai.
                </div>
                {canSee("dipendenti") && onNavigate && (
                  <button onClick={()=>onNavigate({page:"dipendenti"})}
                    style={{marginTop:8,padding:"6px 14px",background:"#dc2626",color:"#fff",border:"none",borderRadius:7,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                    Vai a Dipendenti →
                  </button>
                )}
              </div>
            </div>
          )}

          {/* KPI riepilogo — sempre visibili anche senza operativi */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:12,marginBottom:20}}>
            {[
              {
                label:"Operativi in organico",
                val: dipendenti.filter(d=>d.ruolo==="Operativo").length,
                sub: dipendenti.filter(d=>d.ruolo==="Operativo"&&d.nexoraUid).length + " con account Nexiora",
                col: dipendenti.filter(d=>d.ruolo==="Operativo").length>0?"#7c3aed":"#dc2626",
                emoji:"👷"
              },
              {
                label:"Commesse attive",
                val: projects.filter(p=>!p.isParent&&!p.archived).length,
                sub: projects.filter(p=>!p.isParent&&!p.archived&&p.dataInizio&&p.dataFine&&(p.operaiNecessari||(p.squadra&&p.squadra.length>0))).length + " con piano di carico",
                col:"#2563eb", emoji:"📁"
              },
              {
                label:"Preesistenti in corso",
                val: commessePreesistenti.length,
                sub: "commesse non ancora in Nexiora",
                col:"#d97706", emoji:"⚠️"
              },
              {
                label:"Saturazione settimana corrente",
                val: nessunOperativo ? "N/D" : `${analisiSettimane[0]?.percentualeOccupata||0}%`,
                sub: nessunOperativo ? "aggiungi operativi" : (analisiSettimane[0]?.percentualeOccupata>=80?"carico elevato":"nella norma"),
                col: nessunOperativo?"#9ca3af":analisiSettimane[0]?.percentualeOccupata>=80?"#dc2626":"#059669",
                emoji:"📊"
              },
            ].map(({label,val,col,emoji,sub})=>(
              <div key={label} style={{background:"#fff",border:`1px solid ${col}22`,borderRadius:10,padding:14,textAlign:"center"}}>
                <div style={{fontSize:20,marginBottom:4}}>{emoji}</div>
                <div style={{fontSize:20,fontWeight:800,color:col}}>{val}</div>
                <div style={{fontSize:10,color:"#9ca3af",marginTop:2}}>{label}</div>
                {sub && <div style={{fontSize:9,color:"#d1d5db",marginTop:2}}>{sub}</div>}
              </div>
            ))}
          </div>

          {/* Compatibilità pianificazione — solo se ci sono operativi */}
          {!nessunOperativo && (
            <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:12,padding:16,marginBottom:16}}>
              <div style={{fontSize:12,fontWeight:700,color:"#374151",marginBottom:10}}>📋 Saturazione settimane (ore impegnate / capacità totale)</div>
              {analisiSettimane.map((sett,i)=>{
                const pct = sett.percentualeOccupata;
                const col = pct>=90?"#dc2626":pct>=60?"#d97706":"#059669";
                return (
                  <div key={i} style={{display:"flex",alignItems:"center",gap:10,marginBottom:6}}>
                    <div style={{fontSize:11,color:"#6b7280",width:120,flexShrink:0}}>{sett.label.split("—")[0].trim()}</div>
                    <div style={{flex:1,background:"#f3f4f6",borderRadius:4,height:8}}>
                      <div style={{background:col,borderRadius:4,height:8,width:`${Math.min(100,pct)}%`,transition:"width .3s"}}/>
                    </div>
                    <div style={{fontSize:11,fontWeight:700,color:col,width:50,textAlign:"right"}}>{sett.oreOccupate}h/{sett.maxOreTeam}h</div>
                    <div style={{fontSize:11,fontWeight:700,color:col,width:40,textAlign:"right"}}>{pct}%</div>
                    <div style={{fontSize:10,color:col,width:70,flexShrink:0}}>
                      {pct>=90?"🔴 Critico":pct>=60?"🟡 Elevato":"🟢 OK"}
                    </div>
                  </div>
                );
              })}
              <div style={{fontSize:10,color:"#9ca3af",marginTop:8,fontStyle:"italic"}}>
                Capacità massima calcolata su {dipendenti.filter(d=>d.ruolo==="Operativo").length} operativi × 5 giorni × 8 ore = {dipendenti.filter(d=>d.ruolo==="Operativo").length*40}h/settimana
              </div>
            </div>
          )}

          {/* Figure professionali */}
          <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:12,padding:16,marginBottom:16}}>
            <div style={{fontSize:12,fontWeight:700,color:"#374151",marginBottom:10}}>👷 Organico operativo</div>
            {dipendenti.filter(d=>d.ruolo==="Operativo").length === 0 ? (
              <div style={{fontSize:12,color:"#dc2626",fontWeight:600}}>
                Nessun dipendente con categoria operativa Operativo. Vai in Dipendenti e imposta la categoria operativa.
              </div>
            ) : (
              Object.entries(
                dipendenti.filter(d=>d.ruolo==="Operativo").reduce((acc,d)=>{
                  const m = d.mansione||"Non specificata";
                  if(!acc[m]) acc[m]={totale:0,livelli:[],collegati:0};
                  acc[m].totale++;
                  if(d.livelloProfessionalita) acc[m].livelli.push(parseInt(d.livelloProfessionalita));
                  if(d.nexoraUid) acc[m].collegati++;
                  return acc;
                },{})
              ).map(([mansione,{totale,livelli,collegati}])=>{
                const lvMedio = livelli.length>0?Math.round(livelli.reduce((a,b)=>a+b,0)/livelli.length):0;
                const li = LIVELLI_PROF[lvMedio];
                return (
                  <div key={mansione} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 0",borderBottom:"1px solid #f3f4f6"}}>
                    <span style={{flex:1,fontSize:12,color:"#374151",fontWeight:600}}>{mansione}</span>
                    <span style={{fontSize:11,color:"#6b7280"}}>{totale} {totale===1?"persona":"persone"}</span>
                    <span style={{fontSize:10,color:collegati===totale?"#059669":"#d97706"}}>
                      {collegati}/{totale} in Nexiora
                    </span>
                    {li&&lvMedio>0&&<span style={{fontSize:10,fontWeight:700,padding:"1px 6px",borderRadius:8,background:li.col+"18",color:li.col}}>Liv.{lvMedio}</span>}
                  </div>
                );
              })
            )}
          </div>

          {/* Criticità organizzative */}
          <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:12,padding:16}}>
            <div style={{fontSize:12,fontWeight:700,color:"#374151",marginBottom:10}}>⚡ Criticità rilevate</div>
            {(()=>{
              const critici = [];
              if(nessunOperativo)
                critici.push({tipo:"critico", msg:"🔴 Nessun dipendente con categoria operativa Operativo — la pianificazione non può funzionare"});
              else if(dipendenti.filter(d=>d.ruolo==="Operativo"&&d.nexoraUid).length===0)
                critici.push({tipo:"critico", msg:"🔴 Nessun operativo collegato a un account Nexiora — la programmazione settimanale è vuota"});
              if(analisiSettimane.some(s=>s.percentualeOccupata>=90))
                critici.push({tipo:"alto", msg:"🟠 Alcune settimane sono al limite della saturazione (≥90%) — rischio di non poter accettare nuovi lavori"});
              if(dipendenti.filter(d=>!d.ruolo).length>0)
                critici.push({tipo:"medio", msg:`⚠️ ${dipendenti.filter(d=>!d.ruolo).length} dipendente/i senza ruolo — imposta Operativo o Non operativo`});
              const senzaPiano = projects.filter(p=>!p.isParent&&!p.archived&&(!p.dataInizio||!p.dataFine||(!p.operaiNecessari&&(!p.squadra||p.squadra.length===0)))).length;
              if(senzaPiano>0)
                critici.push({tipo:"medio", msg:`⚠️ ${senzaPiano} commessa/e attiva/e senza piano di carico completo (date + operai necessari)`});
              if(dipendenti.filter(d=>d.ruolo==="Operativo"&&!d.livelloProfessionalita).length>0)
                critici.push({tipo:"basso", msg:`ℹ️ ${dipendenti.filter(d=>d.ruolo==="Operativo"&&!d.livelloProfessionalita).length} operaio/i senza livello professionalità — il simulatore non potrà filtrare per qualifica`});
              if(commessePreesistenti.length===0&&projects.filter(p=>!p.isParent).length>0)
                critici.push({tipo:"info", msg:"ℹ️ Suggerimento: se hai commesse già in corso non ancora in Nexiora, inseriscile come Commesse preesistenti"});

              const colori = {critico:"#fee2e2", alto:"#fff7ed", medio:"#fef3c7", basso:"#f0f9ff", info:"#f9fafb"};
              const border = {critico:"#fca5a5", alto:"#fed7aa", medio:"#fde68a", basso:"#bae6fd", info:"#e5e7eb"};
              const testo = {critico:"#991b1b", alto:"#9a3412", medio:"#92400e", basso:"#0369a1", info:"#6b7280"};

              if(critici.length===0) return (
                <div style={{padding:"10px 14px",background:"#d1fae5",border:"1px solid #6ee7b7",borderRadius:8,fontSize:12,color:"#065f46",fontWeight:600}}>
                  ✅ Nessuna criticità organizzativa rilevata — dati completi e aggiornati
                </div>
              );
              return critici.map((c,i)=>(
                <div key={i} style={{padding:"8px 12px",background:colori[c.tipo],border:`1px solid ${border[c.tipo]}`,borderRadius:8,fontSize:11,color:testo[c.tipo],marginBottom:6,lineHeight:1.5}}>
                  {c.msg}
                </div>
              ));
            })()}
          </div>
        </div>
      )}

      {/* ══ TAB AFFIDABILITÀ ══ */}
      {activeTab==="affidabilita" && (
        <div style={{padding:24}}>
          <h3 style={{fontSize:15,fontWeight:800,marginBottom:4,color:"#111827"}}>🎯 Affidabilità della Simulazione</h3>
          <p style={{fontSize:12,color:"#9ca3af",marginBottom:20}}>Indica quanto le simulazioni di Nexiora siano attendibili in base alla qualità dei dati inseriti.</p>
          <div style={{display:"flex",gap:24,flexWrap:"wrap",marginBottom:24}}>
            {/* Indicatore principale */}
            <div style={{background:"#fff",border:`3px solid ${indiceAffidabilita.colore}`,borderRadius:16,padding:28,textAlign:"center",minWidth:200}}>
              <div style={{fontSize:48,fontWeight:900,color:indiceAffidabilita.colore,marginBottom:4}}>{indiceAffidabilita.score}%</div>
              <div style={{fontSize:18,marginBottom:4}}>{indiceAffidabilita.emoji}</div>
              <div style={{fontSize:14,fontWeight:700,color:indiceAffidabilita.colore,textTransform:"capitalize"}}>Affidabilità {indiceAffidabilita.livello}</div>
            </div>
            {/* Motivazioni */}
            <div style={{flex:1,minWidth:280}}>
              {indiceAffidabilita.bloccante ? (
                <div style={{background:"#fef2f2",border:"1px solid #fca5a5",borderRadius:12,padding:20}}>
                  <div style={{fontSize:14,fontWeight:700,color:"#dc2626",marginBottom:4}}>🚫 Calcolo non disponibile</div>
                  <div style={{fontSize:12,color:"#991b1b",lineHeight:1.6}}>{indiceAffidabilita.note[0]}</div>
                </div>
              ) : indiceAffidabilita.note.length===0 ? (
                <div style={{background:"#d1fae5",border:"1px solid #6ee7b7",borderRadius:12,padding:20}}>
                  <div style={{fontSize:14,fontWeight:700,color:"#059669",marginBottom:4}}>✅ Dati ottimali</div>
                  <div style={{fontSize:12,color:"#065f46"}}>Tutti i dati necessari sono presenti e aggiornati. Le simulazioni sono altamente attendibili.</div>
                </div>
              ) : (
                <div>
                  <div style={{fontSize:12,fontWeight:700,color:"#374151",marginBottom:10}}>Come migliorare l'affidabilità:</div>
                  {indiceAffidabilita.note.map((n,i)=>{
                    const isInfo = n.startsWith("Suggerimento");
                    return (
                      <div key={i} style={{display:"flex",gap:8,alignItems:"flex-start",padding:"8px 12px",
                        background:isInfo?"#f0f9ff":"#fef3c7",
                        border:`1px solid ${isInfo?"#bae6fd":"#fde68a"}`,
                        borderRadius:8,marginBottom:6}}>
                        <span style={{color:isInfo?"#0369a1":"#d97706",flexShrink:0}}>{isInfo?"ℹ️":"⚠️"}</span>
                        <span style={{fontSize:12,color:isInfo?"#0369a1":"#92400e"}}>{n}</span>
                      </div>
                    );
                  })}
                </div>
              )}
              <div style={{marginTop:16,background:"#f0f9ff",border:"1px solid #bae6fd",borderRadius:10,padding:14}}>
                <div style={{fontSize:11,fontWeight:700,color:"#0369a1",marginBottom:6}}>💡 Come migliorare l'affidabilità</div>
                <div style={{fontSize:11,color:"#0369a1",lineHeight:1.7}}>
                  Compila la programmazione settimanale con almeno 4 settimane di anticipo → imposta la categoria operativa per tutti i dipendenti → inserisci il piano di carico completo per ogni commessa → inserisci le commesse pregresse nella sezione dedicata.
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      </div>{/* fine contenuto tab */}

      {/* Sezione commesse preesistenti — sempre visibile sotto i tab */}
        {(isOrgAdmin||isPlatformOwner) && (
          <div style={{borderTop:"1px solid #e5e7eb",padding:"10px 16px",background:"#fff",flexShrink:0}}>
            <button onClick={()=>setShowPreesistenti(s=>!s)}
              style={{display:"flex",alignItems:"center",gap:8,background:"none",border:"none",cursor:"pointer",color:"#374151",fontSize:12,fontWeight:700,fontFamily:"'Inter',sans-serif",width:"100%",padding:0}}>
              <AlertTriangle size={13} color="#f59e0b"/>
              Inserisci Commesse in corso (Non ancora in Nexiora)
              <span style={{marginLeft:"auto",fontSize:11,color:"#9ca3af"}}>{commessePreesistenti.length} inserite</span>
              <span style={{fontSize:11,color:"#9ca3af"}}>{showPreesistenti?"▲":"▼"}</span>
            </button>

            {showPreesistenti && (
              <div style={{marginTop:12}}>
                <div style={{fontSize:11,color:"#9ca3af",marginBottom:10}}>
                  Inserisci le commesse già in essere non ancora in Nexiora. Il simulatore le considererà nel calcolo disponibilità. Eliminale tu quando vengono archiviate o terminate.
                </div>

                {/* Lista commesse preesistenti */}
                {commessePreesistenti.map(cp=>{
                  const LIVELLI_LABEL = {0:"Qualsiasi",1:"Inesperto+",2:"Poco esp.+",3:"Autonomo+",4:"Esperto+",5:"Solo molto esp."};
                  return (
                    <div key={cp.id} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 10px",background:"#fef3c7",border:"1px solid #fde68a",borderRadius:8,marginBottom:6,fontSize:12}}>
                      <span style={{flex:1,fontWeight:600,color:"#92400e"}}>{cp.nome||"—"}</span>
                      <span style={{color:"#6b7280",whiteSpace:"nowrap",fontSize:11}}>{squadraToString(normalizzaSquadra({squadra:cp.squadra,operaiNecessari:cp.operai,livelloMin:cp.livelloMin||0}))}</span>
                      <span style={{color:"#6b7280",whiteSpace:"nowrap"}}>fino {new Date(cp.dataFine+"T00:00:00").toLocaleDateString("it-IT",{day:"numeric",month:"short"})}</span>
                      <button onClick={async()=>{ await deleteDoc(doc(db,"commessePreesistenti",cp.id)); }}
                        style={{background:"none",border:"none",cursor:"pointer",color:"#9ca3af",padding:2,display:"flex"}}
                        onMouseOver={e=>e.currentTarget.style.color="#dc2626"}
                        onMouseOut={e=>e.currentTarget.style.color="#9ca3af"}>
                        <X size={13}/>
                      </button>
                    </div>
                  );
                })}


                {/* Form nuova preesistente */}
                <div style={{display:"grid",gridTemplateColumns:"2fr 80px 120px 130px auto",gap:6,marginTop:8,alignItems:"end"}}>
                  <div>
                    <div style={{fontSize:10,color:"#9ca3af",marginBottom:2}}>Nome/numero commessa</div>
                    <input value={nuovaPreesistente.nome} onChange={e=>setNuovaPreesistente(n=>({...n,nome:e.target.value}))}
                      placeholder="Es. 45-24 Manutenzione..."
                      style={{width:"100%",padding:"6px 8px",border:"1px solid #e5e7eb",borderRadius:6,fontSize:11,fontFamily:"'Inter',sans-serif",outline:"none"}}/>
                  </div>
                  <div>
                    <div style={{fontSize:10,color:"#9ca3af",marginBottom:2}}>Operai</div>
                    <input type="number" min={1} max={20} value={nuovaPreesistente.operai}
                      onChange={e=>setNuovaPreesistente(n=>({...n,operai:parseInt(e.target.value)||1}))}
                      style={{width:"100%",padding:"6px 8px",border:"1px solid #e5e7eb",borderRadius:6,fontSize:11,fontFamily:"'Inter',sans-serif",outline:"none"}}/>
                  </div>
                  <div>
                    <div style={{fontSize:10,color:"#9ca3af",marginBottom:2}}>Livello min.</div>
                    <select value={nuovaPreesistente.livelloMin} onChange={e=>setNuovaPreesistente(n=>({...n,livelloMin:parseInt(e.target.value)}))}
                      style={{width:"100%",padding:"6px 8px",border:"1px solid #e5e7eb",borderRadius:6,fontSize:11,fontFamily:"'Inter',sans-serif",outline:"none"}}>
                      <option value={0}>Qualsiasi</option>
                      <option value={1}>1 - Inesperto+</option>
                      <option value={2}>2 - Poco esp.+</option>
                      <option value={3}>3 - Autonomo+</option>
                      <option value={4}>4 - Esperto+</option>
                      <option value={5}>5 - Solo molto esp.</option>
                    </select>
                  </div>
                  <div>
                    <div style={{fontSize:10,color:"#9ca3af",marginBottom:2}}>Data fine prevista</div>
                    <input type="date" value={nuovaPreesistente.dataFine} onChange={e=>setNuovaPreesistente(n=>({...n,dataFine:e.target.value}))}
                      style={{width:"100%",padding:"6px 8px",border:"1px solid #e5e7eb",borderRadius:6,fontSize:11,fontFamily:"'Inter',sans-serif",outline:"none"}}/>
                  </div>
                  <button onClick={async()=>{
                    if(!nuovaPreesistente.nome.trim()||!nuovaPreesistente.dataFine) return;
                    await addDoc(collection(db,"commessePreesistenti"),{
                      ...nuovaPreesistente, orgId:effectiveOrgId, createdAt:serverTimestamp()
                    });
                    setNuovaPreesistente({nome:"",operai:1,livelloMin:0,dataFine:""});
                  }}
                    style={{padding:"6px 12px",background:"#7c3aed",border:"none",borderRadius:6,color:"#fff",cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:"'Inter',sans-serif",display:"flex",alignItems:"center",gap:4}}>
                    <Plus size={13}/> Aggiungi
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

      {/* Modal Simula Nuova Commessa */}
      {simModal && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:9000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}
          onClick={e=>e.target===e.currentTarget&&(setSimModal(false),setSimResult(null))}>
          <div style={{background:"#fff",borderRadius:16,width:"100%",maxWidth:640,maxHeight:"90vh",overflowY:"auto",boxShadow:"0 25px 80px rgba(0,0,0,.25)"}}>
            {/* Header modal */}
            <div style={{background:"linear-gradient(135deg,#1e1b4b,#3730a3)",padding:"20px 24px",borderRadius:"16px 16px 0 0",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
              <div>
                <div style={{fontSize:16,fontWeight:800,color:"#fff"}}>▶ Simula Nuova Commessa</div>
                <div style={{fontSize:11,color:"rgba(255,255,255,.5)",marginTop:2}}>Analisi impatto organizzativo — non crea la commessa</div>
              </div>
              <button onClick={()=>{setSimModal(false);setSimResult(null);}} style={{background:"rgba(255,255,255,.1)",border:"none",borderRadius:8,padding:"6px 10px",cursor:"pointer",color:"#fff",fontSize:16}}>✕</button>
            </div>
            <div style={{padding:24}}>
              {!simResult ? (
                // Form simulazione
                <div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:16}}>
                    <div>
                      <label style={{fontSize:11,fontWeight:700,color:"#374151",display:"block",marginBottom:4}}>Cliente</label>
                      <input value={simForm.cliente} onChange={e=>setSimForm(f=>({...f,cliente:e.target.value}))}
                        placeholder="Es. Jcoplastic Srl" style={{width:"100%",padding:"8px 10px",border:"1px solid #e5e7eb",borderRadius:8,fontSize:12,fontFamily:"'Inter',sans-serif",outline:"none",boxSizing:"border-box"}}/>
                    </div>
                    <div>
                      <label style={{fontSize:11,fontWeight:700,color:"#374151",display:"block",marginBottom:4}}>Codice commessa <span style={{fontSize:10,color:"#9ca3af"}}>(es. 56-26)</span></label>
                      <input value={simForm.codiceCommessa} onChange={e=>setSimForm(f=>({...f,codiceCommessa:e.target.value.replace(/[^0-9-]/g,"")}))}
                        placeholder="Es. 56-26" style={{width:"100%",padding:"8px 10px",border:"1px solid #e5e7eb",borderRadius:8,fontSize:12,fontFamily:"monospace",fontWeight:700,outline:"none",boxSizing:"border-box"}}/>
                    </div>
                    <div>
                      <label style={{fontSize:11,fontWeight:700,color:"#374151",display:"block",marginBottom:4}}>Nome commessa</label>
                      <input value={simForm.nome} onChange={e=>setSimForm(f=>({...f,nome:e.target.value}))}
                        placeholder="Es. 60-26 Manutenzione" style={{width:"100%",padding:"8px 10px",border:"1px solid #e5e7eb",borderRadius:8,fontSize:12,fontFamily:"'Inter',sans-serif",outline:"none",boxSizing:"border-box"}}/>
                    </div>
                    <div>
                      <label style={{fontSize:11,fontWeight:700,color:"#374151",display:"block",marginBottom:4}}>Data inizio desiderata</label>
                      <input type="date" value={simForm.dataInizio} onChange={e=>setSimForm(f=>({...f,dataInizio:e.target.value}))}
                        style={{width:"100%",padding:"8px 10px",border:"1px solid #e5e7eb",borderRadius:8,fontSize:12,fontFamily:"'Inter',sans-serif",outline:"none",boxSizing:"border-box"}}/>
                    </div>
                    <div>
                      <label style={{fontSize:11,fontWeight:700,color:"#374151",display:"block",marginBottom:4}}>Durata prevista (giorni lavorativi)</label>
                      <input type="number" min={1} value={simForm.durataDays} onChange={e=>setSimForm(f=>({...f,durataDays:e.target.value}))}
                        style={{width:"100%",padding:"8px 10px",border:"1px solid #e5e7eb",borderRadius:8,fontSize:12,fontFamily:"'Inter',sans-serif",outline:"none",boxSizing:"border-box"}}/>
                    </div>
                    <div>
                      <label style={{fontSize:11,fontWeight:700,color:"#374151",display:"block",marginBottom:4}}>Ore uomo stimate totali</label>
                      <input type="number" min={1} value={simForm.oreUomo} onChange={e=>setSimForm(f=>({...f,oreUomo:e.target.value}))}
                        style={{width:"100%",padding:"8px 10px",border:"1px solid #e5e7eb",borderRadius:8,fontSize:12,fontFamily:"'Inter',sans-serif",outline:"none",boxSizing:"border-box"}}/>
                    </div>
                    <div>
                      <label style={{fontSize:11,fontWeight:700,color:"#374151",display:"block",marginBottom:4}}>Livello professionalità richiesto</label>
                      <select value={simForm.livello} onChange={e=>setSimForm(f=>({...f,livello:parseInt(e.target.value)}))}
                        style={{width:"100%",padding:"8px 10px",border:"1px solid #e5e7eb",borderRadius:8,fontSize:12,fontFamily:"'Inter',sans-serif",outline:"none",boxSizing:"border-box"}}>
                        <option value={0}>Qualsiasi</option>
                        <option value={1}>1 - Inesperto+</option>
                        <option value={2}>2 - Poco esperto+</option>
                        <option value={3}>3 - Autonomo+</option>
                        <option value={4}>4 - Esperto+</option>
                        <option value={5}>5 - Solo molto esperti</option>
                      </select>
                    </div>
                    <div>
                      <label style={{fontSize:11,fontWeight:700,color:"#374151",display:"block",marginBottom:4}}>Priorità</label>
                      <select value={simForm.priorita} onChange={e=>setSimForm(f=>({...f,priorita:e.target.value}))}
                        style={{width:"100%",padding:"8px 10px",border:"1px solid #e5e7eb",borderRadius:8,fontSize:12,fontFamily:"'Inter',sans-serif",outline:"none",boxSizing:"border-box"}}>
                        <option value="bassa">Bassa</option>
                        <option value="normale">Normale</option>
                        <option value="alta">Alta</option>
                        <option value="urgente">Urgente</option>
                      </select>
                    </div>
                  </div>
                  <div style={{background:"#f0f9ff",border:"1px solid #bae6fd",borderRadius:8,padding:"10px 14px",marginBottom:16,fontSize:11,color:"#0369a1"}}>
                    ℹ️ Nexiora calcolerà automaticamente il numero di operai necessari dalle ore uomo e dalla durata. Tutte le altre informazioni verranno recuperate dalla piattaforma.
                  </div>
                  <button onClick={eseguiSimulazione}
                    style={{width:"100%",padding:"12px",background:"#7c3aed",border:"none",borderRadius:10,color:"#fff",fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                    ▶ Esegui Simulazione
                  </button>
                </div>
              ) : (
                // Risultati simulazione
                <div>
                  <div style={{marginBottom:16,padding:"12px 16px",background:"#f8fafc",borderRadius:10,fontSize:12,color:"#6b7280"}}>
                    <strong style={{color:"#374151"}}>{simForm.nome||"Nuova commessa"}</strong>{simForm.cliente?` — ${simForm.cliente}`:""} · {simForm.durataDays} giorni · {simForm.oreUomo}h uomo · {simResult.operaiNecessari} operai necessari
                  </div>

                  {/* Risultati principali */}
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,marginBottom:16}}>
                    {[
                      {label:"Saturazione prevista",val:`${simResult.satPrevista}%`,col:simResult.satPrevista>=90?"#dc2626":simResult.satPrevista>=60?"#d97706":"#059669"},
                      {label:"Capacità residua",val:`${simResult.capacitaResidua}%`,col:simResult.capacitaResidua<10?"#dc2626":simResult.capacitaResidua<40?"#d97706":"#059669"},
                      {label:"Affidabilità sim.",val:`${indiceAffidabilita.score}% ${indiceAffidabilita.emoji}`,col:indiceAffidabilita.colore},
                    ].map(({label,val,col})=>(
                      <div key={label} style={{background:"#fff",border:`1px solid ${col}33`,borderRadius:10,padding:14,textAlign:"center"}}>
                        <div style={{fontSize:18,fontWeight:800,color:col,marginBottom:2}}>{val}</div>
                        <div style={{fontSize:10,color:"#9ca3af"}}>{label}</div>
                      </div>
                    ))}
                  </div>

                  {/* Prima finestra */}
                  <div style={{background:simResult.finestraSimulata?"#d1fae5":"#fee2e2",border:`1px solid ${simResult.finestraSimulata?"#6ee7b7":"#fca5a5"}`,borderRadius:10,padding:14,marginBottom:12}}>
                    {simResult.finestraSimulata ? (
                      <>
                        <div style={{fontSize:12,fontWeight:700,color:"#059669",marginBottom:2}}>✅ Prima finestra disponibile</div>
                        <div style={{fontSize:13,fontWeight:800,color:"#065f46"}}>
                          Settimana del {fmtData(simResult.finestraSimulata.sett.lunedi)} con {simResult.finestraSimulata.operai.map(o=>o.nome).join(", ")}
                        </div>
                      </>
                    ) : (
                      <>
                        <div style={{fontSize:12,fontWeight:700,color:"#dc2626",marginBottom:2}}>❌ Nessuna finestra disponibile</div>
                        <div style={{fontSize:12,color:"#991b1b"}}>Non ci sono risorse sufficienti nelle prossime 8 settimane con i parametri indicati.</div>
                      </>
                    )}
                  </div>

                  {/* Analisi testuale */}
                  <div style={{background:"#f8fafc",border:"1px solid #e5e7eb",borderRadius:10,padding:14,marginBottom:12,fontSize:12,color:"#374151",lineHeight:1.7}}>
                    Sulla base dei dati attualmente presenti in Nexiora, la capacità produttiva residua nelle prossime settimane è pari al <strong>{100-simResult.satMediaAttuale}%</strong>. La nuova commessa richiederebbe una capacità aggiuntiva stimata del <strong>{Math.round((simResult.oreAggiuntive/(analisiSettimane.reduce((a,s)=>a+s.maxOreTeam,0)||1))*100)}%</strong>. {simResult.conflitti.length===0?"L'acquisizione risulta compatibile con la pianificazione attuale.":"Sono stati rilevati potenziali conflitti da valutare."}
                  </div>

                  {/* Conflitti */}
                  {simResult.conflitti.length>0 && (
                    <div style={{marginBottom:12}}>
                      <div style={{fontSize:11,fontWeight:700,color:"#dc2626",marginBottom:6}}>⚠️ Criticità rilevate</div>
                      {simResult.conflitti.map((c,i)=>(
                        <div key={i} style={{padding:"6px 10px",background:"#fee2e2",border:"1px solid #fca5a5",borderRadius:6,fontSize:11,color:"#991b1b",marginBottom:4}}>{c}</div>
                      ))}
                    </div>
                  )}

                  {/* Suggerimenti */}
                  {simResult.suggerimenti.length>0 && (
                    <div style={{marginBottom:16}}>
                      <div style={{fontSize:11,fontWeight:700,color:"#7c3aed",marginBottom:6}}>💡 Suggerimenti operativi</div>
                      {simResult.suggerimenti.map((s,i)=>(
                        <div key={i} style={{padding:"6px 10px",background:"#ede9fe",border:"1px solid #c4b5fd",borderRadius:6,fontSize:11,color:"#4c1d95",marginBottom:4}}>{s}</div>
                      ))}
                      <div style={{fontSize:10,color:"#9ca3af",marginTop:6,fontStyle:"italic"}}>La decisione finale rimane sempre dell'imprenditore.</div>
                    </div>
                  )}

                  {/* Bottoni */}
                  <div style={{display:"flex",gap:10}}>
                    <button onClick={()=>setSimResult(null)}
                      style={{flex:1,padding:"10px",border:"1px solid #e5e7eb",borderRadius:10,background:"#fff",color:"#374151",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                      ← Modifica parametri
                    </button>
                    <button onClick={()=>{
                      setSimModal(false); setSimResult(null);
                      // Naviga alla creazione commessa — passa parametri via sessionStorage
                      sessionStorage.setItem("nexiora_sim_prefill", JSON.stringify({
                        nome: simForm.nome,
                        codiceCommessa: simForm.codiceCommessa||"",
                        cliente: simForm.cliente,
                        dataInizio: simForm.dataInizio||(simResult.finestraSimulata&&simResult.finestraSimulata.sett&&simResult.finestraSimulata.sett.lunedi?toLocalDateStr(simResult.finestraSimulata.sett.lunedi):""),
                        operaiNecessari: simResult.operaiNecessari,
                        livelloMin: simForm.livello,
                        durataDays: simForm.durataDays,
                      }));
                      window.dispatchEvent(new CustomEvent("nexiora:nuova-commessa"));
                    }}
                      style={{flex:1,padding:"10px",border:"none",borderRadius:10,background:"#7c3aed",color:"#fff",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                      ✅ Trasforma in Commessa →
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
